import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, Mail, MessageCircle, MapPin, Download } from "lucide-react";
import SocialLinks from "@/components/common/SocialLinks";
import { useSettings, useMemberCount } from "@/hooks/useSiteQueries";
import { formatMemberCount } from "@/lib/memberCountDisplay";
import { getAgmPdfUrl, getAnnualAccountsPdfUrl, getContactEmails } from "@/lib/settings";
import { LOGO_URL, seedSiteSettings } from "@/data/seed/siteContent";
import { clearAdminSession } from "@/lib/adminSession";
import { SHOW_ANNUAL_ACCOUNTS_PAGE } from "@/lib/siteFeatures";

const FORUM_LINKS = [
  { to: "/our-work/socio-civic-political", label: "Socio Civic & Political Forum" },
  { to: "/our-work/environment", label: "Environment Forum" },
  { to: "/our-work/youth-training", label: "Youth & Training Forum" },
];

const NAV = [
  { to: "/", label: "Home", exact: true },
  { to: "/about", label: "About Us" },
  { to: "/our-work", label: "Forums", dropdown: FORUM_LINKS },
  { to: "/events", label: "Events" },
  { to: "/units", label: "Units" },
  { to: "/sponsors", label: "Sponsors" },
  { to: "/membership", label: "Membership" },
  ...(SHOW_ANNUAL_ACCOUNTS_PAGE ? [{ to: "/annual-accounts", label: "Accounts" }] : []),
  { to: "/contact", label: "Contact" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [workOpen, setWorkOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setOpen(false);
    setWorkOpen(false);
  }, [location.pathname]);

  return (
    <header className="bg-maroon text-white sticky top-0 z-50 shadow-md" data-testid="public-header">
      <div className="container-bcs">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
            <img src={LOGO_URL} alt="The Bombay Catholic Sabha" className="h-12 w-12 rounded-full bg-white/95 p-0.5" />
            <div className="leading-tight">
              <p className="font-serif text-lg sm:text-xl font-semibold tracking-tight">
                The Bombay Catholic Sabha
              </p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold-light">
                Love · Service · Fraternity
              </p>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV.map((item) =>
              item.dropdown ? (
                <div key={item.to} className="relative group">
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `px-3 py-2 text-sm font-medium rounded-md inline-flex items-center gap-1 transition-colors ${
                        isActive ? "text-gold-light" : "text-white/85 hover:text-white"
                      }`
                    }
                    data-testid="nav-our-work"
                  >
                    {item.label} <ChevronDown size={14} />
                  </NavLink>
                  <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150">
                    <div className="bg-white text-ink rounded-lg shadow-xl border border-gray-100 py-2 w-72">
                      {item.dropdown.map((d) => (
                        <Link
                          key={d.to}
                          to={d.to}
                          className="block px-4 py-2.5 text-sm hover:bg-cream-alt hover:text-maroon transition-colors"
                          data-testid={`nav-dropdown-${d.to.split("/").pop()}`}
                        >
                          {d.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.exact}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive ? "text-gold-light" : "text-white/85 hover:text-white"
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <button
            className="lg:hidden p-2 rounded-md hover:bg-white/10 transition-colors"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <nav className="lg:hidden border-t border-white/10 bg-maroon-dark" data-testid="mobile-nav">
          <div className="container-bcs py-3 space-y-1">
            {NAV.map((item) =>
              item.dropdown ? (
                <div key={item.to}>
                  <button
                    className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-white/90"
                    onClick={() => setWorkOpen(!workOpen)}
                    data-testid="mobile-nav-our-work-toggle"
                  >
                    {item.label} <ChevronDown size={15} className={workOpen ? "rotate-180 transition-transform" : "transition-transform"} />
                  </button>
                  {workOpen && (
                    <div className="pl-4 space-y-1 pb-2">
                      <Link to="/our-work" className="block px-3 py-2 text-sm text-white/75 hover:text-white">
                        Forums — Overview
                      </Link>
                      {item.dropdown.map((d) => (
                        <Link key={d.to} to={d.to} className="block px-3 py-2 text-sm text-white/75 hover:text-white">
                          {d.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.exact}
                  className="block px-3 py-2.5 text-sm font-medium text-white/90 hover:text-gold-light"
                >
                  {item.label}
                </NavLink>
              )
            )}
          </div>
        </nav>
      )}
    </header>
  );
}

function Footer({ settings }) {
  const s = settings;
  const { data: memberCount } = useMemberCount();
  const contactEmails = getContactEmails(s);
  const agmPdfUrl = getAgmPdfUrl(s);
  const annualAccountsPdfUrl = getAnnualAccountsPdfUrl(s);
  const memberTotal = memberCount ? formatMemberCount(memberCount) : null;
  return (
    <footer className="bg-maroon-dark text-white mt-auto" data-testid="public-footer">
      <div className="container-bcs py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="BCS" className="h-12 w-12 rounded-full bg-white/95 p-0.5" />
              <p className="font-serif text-lg font-semibold leading-tight">The Bombay Catholic Sabha</p>
            </div>
            <p className="mt-4 text-sm text-white/70 leading-relaxed">
              A recognised laity organisation of the Archdiocese of Bombay
              {memberTotal ? ` with ${memberTotal} life members` : ""}, serving Mumbai with love, service and fraternity.
            </p>
            <div className="mt-6">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-light mb-3">Follow us</p>
              <SocialLinks settings={s} className="text-sm text-white/80" />
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-light mb-4">Explore</p>
            <ul className="space-y-2.5 text-sm text-white/80">
              <li><Link to="/about" className="hover:text-gold-light transition-colors">About Us</Link></li>
              <li><Link to="/events" className="hover:text-gold-light transition-colors">Events</Link></li>
              <li><Link to="/units" className="hover:text-gold-light transition-colors">Unit / Parish Search</Link></li>
              <li><Link to="/sponsors" className="hover:text-gold-light transition-colors">Sponsors</Link></li>
              <li><Link to="/membership" className="hover:text-gold-light transition-colors">Membership Search</Link></li>
              {SHOW_ANNUAL_ACCOUNTS_PAGE && (
                <li>
                  <Link to="/annual-accounts" className="hover:text-gold-light transition-colors">Annual Accounts</Link>
                </li>
              )}
              <li><Link to="/privacy" className="hover:text-gold-light transition-colors">Privacy Policy</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-light mb-4">Forums</p>
            <ul className="space-y-2.5 text-sm text-white/80">
              {FORUM_LINKS.map((f) => (
                <li key={f.to}>
                  <Link to={f.to} className="hover:text-gold-light transition-colors">{f.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-light mb-4">BCS AGM</p>
            <p className="text-sm text-white/70 leading-relaxed mb-4">
              Annual Report 2025–26 for the Bombay Catholic Sabha AGM.
            </p>
            {agmPdfUrl ? (
              <a
                href={agmPdfUrl}
                target="_blank"
                rel="noreferrer"
                download="BCS-Annual-Report.pdf"
                className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-gold-light transition-colors"
                data-testid="footer-agm-pdf-link"
              >
                <Download size={15} className="text-gold-light shrink-0" />
                Download AGM report
              </a>
            ) : (
              <p className="text-sm text-white/55">No AGM report uploaded yet.</p>
            )}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-light mb-4">BCS Annual Accounts</p>
            <p className="text-sm text-white/70 leading-relaxed mb-4">
              Audited annual accounts for download.
            </p>
            {annualAccountsPdfUrl ? (
              <a
                href={annualAccountsPdfUrl}
                target="_blank"
                rel="noreferrer"
                download="BCS-Annual-Accounts.pdf"
                className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-gold-light transition-colors"
                data-testid="footer-annual-accounts-pdf-link"
              >
                <Download size={15} className="text-gold-light shrink-0" />
                Download Annual Accounts
              </a>
            ) : (
              <p className="text-sm text-white/55">No annual accounts PDF uploaded yet.</p>
            )}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-light mb-4">Contact</p>
            <ul className="space-y-3 text-sm text-white/80">
              <li className="flex gap-2.5"><MapPin size={16} className="shrink-0 mt-0.5 text-gold-light" /><span>{s.address}</span></li>
              <li>
                <a href={`tel:${s.contact_phone}`} className="flex items-center gap-2.5 hover:text-gold-light transition-colors" data-testid="footer-phone">
                  <Phone size={15} className="text-gold-light" /> {s.contact_phone}
                </a>
              </li>
              {contactEmails.map((email, i) => (
                <li key={email}>
                  <a
                    href={`mailto:${email}`}
                    className="flex items-center gap-2.5 hover:text-gold-light transition-colors"
                    data-testid={`footer-email-${i}`}
                  >
                    <Mail size={15} className="text-gold-light shrink-0" /> {email}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href={`https://wa.me/${s.whatsapp.replace(/[^0-9]/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2.5 hover:text-gold-light transition-colors"
                  data-testid="footer-whatsapp"
                >
                  <MessageCircle size={15} className="text-gold-light" /> WhatsApp us
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-bcs py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/55">
          <p>© {new Date().getFullYear()} The Bombay Catholic Sabha. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <Link to="/privacy" className="hover:text-gold-light transition-colors" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/admin/login" className="hover:text-gold-light transition-colors" data-testid="footer-admin-link">
              Admin Login
            </Link>
          </div>
          <p>
            Site developed by{" "}
            <a
              href="https://peeparound.com/"
              target="_blank"
              rel="noreferrer"
              className="text-white/70 hover:text-gold-light transition-colors"
              data-testid="footer-peeparound-link"
            >
              Peep Around Private Limited
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout() {
  const { data: settings = seedSiteSettings } = useSettings();

  useEffect(() => {
    clearAdminSession();
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-cream">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer settings={settings} />
    </div>
  );
}
